import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import { on } from '@ember/modifier';
import { concat, fn } from '@ember/helper';
import { t } from 'ember-intl';
import { findBy } from '@nullvoxpopuli/ember-composable-helpers';
import {
  HdsModal,
  HdsTable,
  HdsFormCheckboxBase,
  HdsIcon,
  HdsButtonSet,
  HdsButton,
} from '@hashicorp/design-system-components/components';
import { Flex, Typography } from 'core/components';

class SelectGroupFormState {
  @tracked selectedGroupIds = [];
}

/**
 *
 * `IamGroupsProjectModalSelectGroup` allows the user to select group(s) from a fancy list.
 *
 * ```
 * <Iam::Groups::Project::Modal::SelectGroup
 *   @candidateGroups={{this.model.groups}}
 *   @initialSelectedGroupIds={{this.model.selectedGroupIds}}
 *   @handleSubmit={{this.submitHandler}}
 *   @handleCancel={{this.cancelHandler}}
 * />
 * ```
 *
 * @class IamGroupsProjectModalSelectGroup
 *
 */

export default class IamGroupsProjectModalSelectGroup extends Component {
  formState = new SelectGroupFormState();

  constructor() {
    super(...arguments);

    this.formState.selectedGroupIds = this.args.initialSelectedGroupIds;
  }

  validate(/* formState */) {
    // TODO: Validate that one or more group IDs have been selected ...
  }

  isChecked = (groupId) => {
    return this.formState.selectedGroupIds.includes(groupId);
  };

  toggleGroupChecked = (groupId) => {
    if (this.isChecked(groupId)) {
      this.formState.selectedGroupIds = this.formState.selectedGroupIds.filter(
        (id) => id !== groupId
      );
    } else {
      this.formState.selectedGroupIds =
        this.formState.selectedGroupIds.concat(groupId);
    }
  };

  submit = task(async (event) => {
    event.preventDefault();

    this.validate(this.formState);
    await this.args.handleSubmit.perform(this.formState);
  });

  cancel = () => {
    this.args.handleCancel();
  };

  <template>
    <HdsModal
      @onClose={{this.cancel}}
      @size="large"
      data-test-iam-groups-project-modal-select-group
      as |M|
    >
      <M.Header @icon="file-text">
        {{t "components.iam.groups.project.modal.select-group.header"}}
      </M.Header>
      <M.Body>
        <form
          aria-label={{t
            "components.iam.groups.project.modal.select-group.form.aria-label"
          }}
          id="select-groups-modal"
          novalidate
          {{on "submit" this.submit.perform}}
        >
          <HdsTable @valign="middle" data-test-groups-project-add-table>
            <:head as |H|>
              <H.Tr>
                <H.Th @width="51px" />
                <H.Th>
                  {{t
                    "components.iam.groups.project.modal.select-group.table.name"
                  }}
                </H.Th>
                <H.Th />
              </H.Tr>
            </:head>

            <:body as |B|>
              {{#each @candidateGroups as |group|}}
                <B.Tr>
                  <B.Td>
                    {{#let
                      (if
                        (findBy "id" group.resourceId this.selectedGroupIds)
                        true
                        false
                      )
                    }}
                      <HdsFormCheckboxBase
                        @value={{group.resourceId}}
                        checked={{this.isChecked group.resourceId}}
                        id={{concat "group-checkbox-" group.resourceId}}
                        {{on
                          "change"
                          (fn this.toggleGroupChecked group.resourceId)
                        }}
                        name="group-checkbox"
                        data-test-groups-project-add-table-item
                      />
                    {{/let}}
                  </B.Td>
                  <B.Td>
                    <Flex @alignItems="center" @gap="xs">
                      <HdsIcon @name="users" @isInline={{true}} />
                      <label for={{concat "group-checkbox-" group.resourceId}}>
                        {{group.displayName}}
                      </label>
                    </Flex>
                  </B.Td>
                  <B.Td>
                    <Typography
                      @variant="body1"
                      class="hds-typography-body-100 hds-foreground-faint hds-font-weight-regular"
                    >
                      {{t
                        "components.iam.groups.project.modal.select-group.table.members"
                        memberCount=group.memberCount
                      }}
                    </Typography>
                  </B.Td>
                </B.Tr>
              {{/each}}
            </:body>
          </HdsTable>
        </form>
      </M.Body>

      <M.Footer as |F|>
        <HdsButtonSet>
          <HdsButton
            @disabled={{this.submit.isRunning}}
            @text={{t
              "components.iam.groups.project.modal.select-group.form.submit-button"
            }}
            type="submit"
            form="select-groups-modal"
          />

          <HdsButton
            @color="secondary"
            @text={{t
              "components.iam.groups.project.modal.select-group.form.cancel-button"
            }}
            type="button"
            {{on "click" F.close}}
          />
        </HdsButtonSet>
      </M.Footer>
    </HdsModal>
  </template>
}
